import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCrmPipelines,
  fetchCrmDeals,
  fetchCrmContacts,
  fetchCrmServices,
  createCrmDeal,
  updateCrmDeal,
  deleteCrmDeal,
} from "../features/crm/crmThunks.js";

import "../styles/crm/crmpipelines.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const CrmPipeline = () => {
  const dispatch = useDispatch();

  const crmState = useSelector((state) => state.crm || {});
  const pipelines = crmState.pipelines || [];
  const deals = crmState.deals || [];
  const contacts = crmState.contacts || [];
  const services = crmState.services || [];
  const loading = crmState.loading;
  const error = crmState.error;

  const currentUser = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const userRole = (currentUser?.role || "").toLowerCase();
  const isAdmin = userRole === "admin";
  const isReceptionist = userRole === "receptionist";

  const [activePipelineId, setActivePipelineId] = useState(null);

  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignableUsersError, setAssignableUsersError] = useState(null);

  const [showNewDealForm, setShowNewDealForm] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [estimatedValue, setEstimatedValue] = useState("");
  const [closingDate, setClosingDate] = useState("");
  const [caseTitle, setCaseTitle] = useState("");
  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedOwnerId, setSelectedOwnerId] = useState("");

  // Modal eliminar caso
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dealIdToDelete, setDealIdToDelete] = useState(null);
  const [dealDeletePreview, setDealDeletePreview] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(fetchCrmPipelines(true));
    dispatch(fetchCrmContacts({}));
    dispatch(fetchCrmServices({ onlyActive: true }));
  }, [dispatch]);

  useEffect(() => {
    if (!isAdmin || !token) return;

    const fetchAssignableUsers = async () => {
      try {
        setAssignableUsersError(null);

        const response = await fetch(`${API_BASE_URL}/crm/assignable-users/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json().catch(() => []);
        if (!response.ok) {
          setAssignableUsersError(
            data?.detail || "No se pudieron cargar los usuarios asignables."
          );
          setAssignableUsers([]);
          return;
        }

        setAssignableUsers(Array.isArray(data) ? data : []);
      } catch (e) {
        setAssignableUsersError("No se pudieron cargar los usuarios asignables.");
        setAssignableUsers([]);
      }
    };

    fetchAssignableUsers();
  }, [isAdmin, token]);

  const receptionists = useMemo(() => {
    return (assignableUsers || []).filter((u) => u.role === "receptionist");
  }, [assignableUsers]);

  useEffect(() => {
    if (!activePipelineId && pipelines.length > 0) {
      const firstPipelineId = pipelines[0].id;
      setActivePipelineId(firstPipelineId);
      dispatch(fetchCrmDeals({ pipelineId: firstPipelineId }));
    }
  }, [pipelines, activePipelineId, dispatch]);

  const activePipeline = useMemo(() => {
    if (!activePipelineId) return null;
    return pipelines.find((pipeline) => pipeline.id === activePipelineId) || null;
  }, [pipelines, activePipelineId]);

  const sortedStages = useMemo(() => {
    return (activePipeline?.stages || []).slice().sort((a, b) => a.order - b.order);
  }, [activePipeline]);

  const handlePipelineChange = (event) => {
    const newPipelineId = Number(event.target.value);
    setActivePipelineId(newPipelineId);
    dispatch(fetchCrmDeals({ pipelineId: newPipelineId }));
  };

  const dealsByStageId = useMemo(() => {
    const grouped = {};
    deals.forEach((deal) => {
      const stageId = deal.stage;
      if (!grouped[stageId]) grouped[stageId] = [];
      grouped[stageId].push(deal);
    });
    return grouped;
  }, [deals]);

  const contactsById = useMemo(() => {
    const map = {};
    contacts.forEach((contact) => {
      map[contact.id] = contact;
    });
    return map;
  }, [contacts]);

  const servicesById = useMemo(() => {
    const map = {};
    services.forEach((service) => {
      map[service.id] = service;
    });
    return map;
  }, [services]);

  const handleToggleNewDealForm = () => {
    setFormError(null);
    setShowNewDealForm((prev) => !prev);
  };

  const handleCreateDealSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);

    if (!activePipeline) {
      setFormError("No hay pipeline seleccionado.");
      return;
    }

    if (sortedStages.length === 0) {
      setFormError("El pipeline no tiene fases configuradas.");
      return;
    }

    const firstStageId = sortedStages[0].id;

    if (!selectedContactId) {
      setFormError("Selecciona un paciente o lead.");
      return;
    }
    if (!selectedServiceId) {
      setFormError("Selecciona una cirugía / procedimiento.");
      return;
    }
    if (!estimatedValue) {
      setFormError("Indica un presupuesto estimado.");
      return;
    }

    const contactObj = contactsById[Number(selectedContactId)];
    const serviceObj = servicesById[Number(selectedServiceId)];

    let finalTitle = caseTitle.trim();
    if (!finalTitle) {
      const contactName = contactObj ? contactObj.full_name : "Caso";
      const serviceName = serviceObj ? serviceObj.name : "";
      finalTitle = serviceName ? `${contactName} - ${serviceName}` : contactName;
    }

    const payload = {
      title: finalTitle,
      contact: Number(selectedContactId),
      service: Number(selectedServiceId),
      pipeline: activePipelineId,
      stage: firstStageId,
      status: "open",
      estimated_value: estimatedValue,
    };

    if (closingDate) {
      payload.expected_closing_date = closingDate;
    }

    if (isAdmin) {
      payload.owner = selectedOwnerId ? Number(selectedOwnerId) : null;
    }

    setIsSubmitting(true);
    const resultAction = await dispatch(createCrmDeal(payload));

    if (createCrmDeal.fulfilled.match(resultAction)) {
      setSelectedContactId("");
      setSelectedServiceId("");
      setEstimatedValue("");
      setClosingDate("");
      setCaseTitle("");
      setSelectedOwnerId("");
      setShowNewDealForm(false);
      dispatch(fetchCrmDeals({ pipelineId: activePipelineId }));
    } else {
      const err = resultAction.payload || { detail: "No se pudo crear el caso." };
      setFormError(err.detail || "No se pudo crear el caso.");
    }

    setIsSubmitting(false);
  };

  const handleMoveDeal = async (deal, direction) => {
    if (!activePipeline) return;

    const currentIndex = sortedStages.findIndex((stage) => stage.id === deal.stage);
    if (currentIndex === -1) return;

    let targetStage = null;
    if (direction === "prev" && currentIndex > 0) targetStage = sortedStages[currentIndex - 1];
    if (direction === "next" && currentIndex < sortedStages.length - 1)
      targetStage = sortedStages[currentIndex + 1];
    if (!targetStage) return;

    const resultAction = await dispatch(
      updateCrmDeal({ dealId: deal.id, dealData: { stage: targetStage.id } })
    );

    if (!updateCrmDeal.fulfilled.match(resultAction)) {
      window.alert("No se pudo mover el caso de fase.");
    }
  };

  const handleChangeDealOwner = async (dealId, ownerIdRaw) => {
    if (!isAdmin) return;

    const ownerValue = ownerIdRaw ? Number(ownerIdRaw) : null;

    const resultAction = await dispatch(
      updateCrmDeal({ dealId, dealData: { owner: ownerValue } })
    );

    if (!updateCrmDeal.fulfilled.match(resultAction)) {
      const err = resultAction.payload || { detail: "No se pudo reasignar el responsable." };
      window.alert(err.detail || "No se pudo reasignar el responsable.");
    }
  };

  const handleOpenDeleteDealModal = (deal) => {
    const contactObj = contactsById[deal.contact];
    const serviceObj = servicesById[deal.service];

    const contactName = contactObj ? contactObj.full_name : "Sin paciente";
    const serviceName = serviceObj ? serviceObj.name : "Sin cirugía";

    setDealIdToDelete(deal.id);
    setDealDeletePreview(`${contactName} · ${serviceName}`);
    setDeleteErrorMessage(null);
    setIsDeleting(false);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteDealModal = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setDealIdToDelete(null);
    setDealDeletePreview("");
    setDeleteErrorMessage(null);
    setIsDeleting(false);
  };

  useEffect(() => {
    if (!isDeleteModalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleCloseDeleteDealModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDeleteModalOpen, isDeleting]);

  const handleConfirmDeleteDeal = async () => {
    if (!dealIdToDelete) return;

    setIsDeleting(true);
    setDeleteErrorMessage(null);

    const resultAction = await dispatch(deleteCrmDeal(dealIdToDelete));

    if (deleteCrmDeal.fulfilled.match(resultAction)) {
      handleCloseDeleteDealModal();
      dispatch(fetchCrmDeals({ pipelineId: activePipelineId }));
      return;
    }

    const err = resultAction.payload || { detail: "No se pudo eliminar el caso." };
    setDeleteErrorMessage(err.detail || "No se pudo eliminar el caso.");
    setIsDeleting(false);
  };

  if (loading && pipelines.length === 0) return <div>Cargando pipeline...</div>;
  if (error) return <div>Error al cargar pipeline: {error.detail || "Error"}</div>;

  if (!pipelines || pipelines.length === 0) {
    if (isReceptionist) return <p>No tienes pipelines asignados.</p>;
    return <p>No hay pipelines definidos.</p>;
  }

  return (
    <div className="crm-pipeline-container">
      <div className="crm-pipeline-header">
        <div>
          <h2>Pipeline</h2>
          <p className="crm-pipeline-subtitle">
            Visualiza y gestiona casos (nombre, cirugía, presupuesto) como en un
            CRM.
          </p>
        </div>

        <div className="crm-pipeline-header-actions">
          <div className="crm-pipeline-selector">
            <label htmlFor="pipeline-select">Pipeline:</label>
            <select
              id="pipeline-select"
              value={activePipelineId || ""}
              onChange={handlePipelineChange}
            >
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={handleToggleNewDealForm}
          >
            {showNewDealForm ? "Cerrar" : "Nuevo caso"}
          </button>
        </div>
      </div>

      {showNewDealForm && (
        <div className="crm-new-deal-form-wrapper">
          {contacts.length === 0 && (
            <div className="crm-new-deal-hint">
              Primero crea al menos un contacto en la pestaña{" "}
              <strong>Contactos</strong>.
            </div>
          )}

          <form className="crm-new-deal-form" onSubmit={handleCreateDealSubmit}>
            <div className="crm-new-deal-row">
              <div className="crm-new-deal-field">
                <label htmlFor="deal-contact">Paciente / lead</label>
                <select
                  id="deal-contact"
                  value={selectedContactId}
                  onChange={(e) => setSelectedContactId(e.target.value)}
                  disabled={contacts.length === 0}
                >
                  <option value="">Selecciona paciente / lead</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.full_name}{" "}
                      {contact.email ? `(${contact.email})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="crm-new-deal-field">
                <label htmlFor="deal-service">Cirugía / procedimiento</label>
                <select
                  id="deal-service"
                  value={selectedServiceId}
                  onChange={(e) => setSelectedServiceId(e.target.value)}
                  disabled={services.length === 0}
                >
                  <option value="">Selecciona cirugía</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="crm-new-deal-field">
                <label htmlFor="deal-value">Presupuesto (€)</label>
                <input
                  id="deal-value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={estimatedValue}
                  onChange={(e) => setEstimatedValue(e.target.value)}
                  placeholder="Ej: 4500"
                />
              </div>
            </div>

            <div className="crm-new-deal-row">
              {isAdmin ? (
                <div className="crm-new-deal-field">
                  <label htmlFor="deal-owner">Responsable</label>
                  <select
                    id="deal-owner"
                    value={selectedOwnerId}
                    onChange={(e) => setSelectedOwnerId(e.target.value)}
                  >
                    <option value="">Sin asignar (lo llevo yo)</option>
                    {receptionists.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.display_name || u.email || `Recepcionista ${u.id}`}
                      </option>
                    ))}
                  </select>

                  {assignableUsersError && (
                    <div
                      className="crm-new-deal-hint"
                      style={{ marginTop: "0.2rem" }}
                    >
                      {assignableUsersError}
                    </div>
                  )}
                </div>
              ) : (
                <div className="crm-new-deal-field">
                  <label>Responsable</label>
                  <input value="Tú" disabled />
                </div>
              )}

              <div className="crm-new-deal-field">
                <label htmlFor="deal-title">Nombre del caso (opcional)</label>
                <input
                  id="deal-title"
                  type="text"
                  value={caseTitle}
                  onChange={(e) => setCaseTitle(e.target.value)}
                  placeholder="Ej: Laura Gómez - Aumento de pecho"
                />
              </div>

              <div className="crm-new-deal-field">
                <label htmlFor="deal-date">
                  Fecha estimada de cirugía (opcional)
                </label>
                <input
                  id="deal-date"
                  type="date"
                  value={closingDate}
                  onChange={(e) => setClosingDate(e.target.value)}
                />
              </div>
            </div>

            {formError && <div className="crm-new-deal-error">{formError}</div>}

            <div className="crm-new-deal-actions">
              <button
                type="submit"
                className="btn-primary btn-sm"
                disabled={isSubmitting || contacts.length === 0}
              >
                {isSubmitting ? "Creando..." : "Crear caso"}
              </button>
              <button
                type="button"
                className="ghost btn-sm"
                onClick={handleToggleNewDealForm}
              >
                Cancelar
              </button>
            </div>

            <p className="crm-new-deal-note">
              El caso se crea en la <strong>primera fase</strong> del pipeline.
            </p>
          </form>
        </div>
      )}

      {!activePipeline ? (
        <p>No se ha podido seleccionar un pipeline activo.</p>
      ) : (
        <div className="crm-pipeline-board">
          {sortedStages.map((stage, index) => {
            const stageDeals = dealsByStageId[stage.id] || [];
            const hasPrevStage = index > 0;
            const hasNextStage = index < sortedStages.length - 1;

            return (
              <div className="crm-pipeline-column" key={stage.id}>
                <div className="crm-pipeline-column-header">
                  <span className="crm-pipeline-column-title">
                    {stage.name}
                  </span>
                  <span className="crm-pipeline-column-count">
                    {stageDeals.length}
                  </span>
                </div>

                <div className="crm-pipeline-column-body">
                  {stageDeals.length === 0 ? (
                    <div className="crm-pipeline-empty">
                      Sin casos en esta fase
                    </div>
                  ) : (
                    stageDeals.map((deal) => {
                      const contactObj = contactsById[deal.contact];
                      const serviceObj = servicesById[deal.service];

                      const contactName = contactObj
                        ? contactObj.full_name
                        : "Sin paciente";
                      const serviceName = serviceObj
                        ? serviceObj.name
                        : "Sin cirugía";

                      return (
                        <div className="crm-pipeline-card" key={deal.id}>
                          <button
                            type="button"
                            className="crm-pipeline-card-delete"
                            onClick={() => handleOpenDeleteDealModal(deal)}
                            title="Eliminar caso"
                          >
                            ×
                          </button>

                          <div className="crm-pipeline-card-title">
                            {contactName}
                          </div>
                          <div className="crm-pipeline-card-subtitle">
                            {serviceName}
                          </div>

                          <div className="crm-pipeline-card-row crm-deal-owner-row">
                            <span className="crm-pipeline-card-label">
                              Responsable:
                            </span>

                            {isAdmin ? (
                              <select
                                className="crm-deal-owner-select-inline"
                                value={deal.owner ? String(deal.owner) : ""}
                                onChange={(e) =>
                                  handleChangeDealOwner(deal.id, e.target.value)
                                }
                              >
                                <option value="">
                                  Sin asignar (lo llevo yo)
                                </option>
                                {receptionists.map((u) => (
                                  <option key={u.id} value={u.id}>
                                    {u.display_name ||
                                      u.email ||
                                      `Recepcionista ${u.id}`}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="crm-pipeline-card-value">
                                {deal.owner ? "Tú" : "Sin asignar"}
                              </span>
                            )}
                          </div>

                          <div className="crm-pipeline-card-row">
                            <span className="crm-pipeline-card-label">
                              Presupuesto:
                            </span>
                            <span className="crm-pipeline-card-value">
                              {deal.estimated_value
                                ? `${deal.estimated_value} €`
                                : "-"}
                            </span>
                          </div>

                          <div className="crm-pipeline-card-row">
                            <span className="crm-pipeline-card-label">
                              Estado:
                            </span>
                            <span className="crm-pipeline-card-value">
                              {deal.status}
                            </span>
                          </div>

                          {deal.expected_closing_date && (
                            <div className="crm-pipeline-card-row">
                              <span className="crm-pipeline-card-label">
                                Fecha estimada:
                              </span>
                              <span className="crm-pipeline-card-value">
                                {deal.expected_closing_date}
                              </span>
                            </div>
                          )}

                          <div className="crm-pipeline-card-footer">
                            {hasPrevStage && (
                              <button
                                type="button"
                                className="ghost btn-xs"
                                onClick={() => handleMoveDeal(deal, "prev")}
                              >
                                ←
                              </button>
                            )}
                            {hasNextStage && (
                              <button
                                type="button"
                                className="btn-primary btn-xs"
                                onClick={() => handleMoveDeal(deal, "next")}
                              >
                                →
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isDeleteModalOpen ? (
        <div
          className="ui-modal-overlay"
          onMouseDown={handleCloseDeleteDealModal}
          role="presentation"
        >
          <div
            className="ui-modal"
            onMouseDown={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="ui-modal-header">
              <h3>Eliminar caso</h3>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help">
                ¿Seguro que quieres eliminar este caso?
              </div>
              <div className="ui-delete-subhelp">{dealDeletePreview}</div>

              {deleteErrorMessage ? (
                <div className="ui-modal-error">{deleteErrorMessage}</div>
              ) : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={handleCloseDeleteDealModal}
                disabled={isDeleting}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={handleConfirmDeleteDeal}
                disabled={isDeleting}
              >
                {isDeleting ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CrmPipeline;